import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Icon } from './Icon';
import type { SelectOption } from './Select';

export interface MultiSelectProps {
  label: string;
  hideLabel?: boolean;
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  hint?: string;
  error?: string | null;
  disabled?: boolean;
  /** Show a filter box once the list passes this length. */
  searchThreshold?: number;
  className?: string;
  id?: string;
}

/**
 * Checkbox listbox in a popover. Implemented by hand rather than with a native
 * multiple-select because the reference filter bars need a summary trigger
 * ("Domain: 3 selected") and native multi-selects cannot show one.
 */
export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(function MultiSelect(
  {
    label,
    hideLabel,
    options,
    value,
    onChange,
    placeholder = 'All',
    hint,
    error,
    disabled,
    searchThreshold = 8,
    className,
    id,
  },
  ref,
) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const listId = `${baseId}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const filtered = options.filter((o) =>
    query ? o.label.toLowerCase().includes(query.toLowerCase()) : true,
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function toggle(optionValue: string) {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  }

  function onListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(filtered.length - 1);
    } else if (event.key === ' ' || event.key === 'Enter') {
      const option = filtered[activeIndex];
      if (option && !option.disabled) {
        event.preventDefault();
        toggle(option.value);
      }
    }
  }

  const summary =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? '1 selected')
        : `${value.length} selected`;

  return (
    <div ref={containerRef} className={cn('relative flex flex-col gap-1.5', className)}>
      <span
        id={`${baseId}-label`}
        className={cn('text-dense-lg font-medium text-content-secondary', hideLabel && 'sr-only')}
      >
        {label}
      </span>
      <button
        ref={(node) => {
          triggerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={`${baseId}-label ${baseId}-trigger`}
        id={`${baseId}-trigger`}
        aria-invalid={Boolean(error) || undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          'flex h-9 items-center justify-between gap-2 rounded-md border border-line bg-surface px-3 text-body text-content',
          'transition-colors duration-fast ease-standard hover:border-line-strong',
          'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-content-disabled',
          'aria-[invalid=true]:border-danger',
          focusRing,
        )}
      >
        <span className={cn('truncate', value.length === 0 && 'text-content-muted')}>
          {summary}
        </span>
        <span className="flex items-center gap-1">
          {value.length > 0 ? (
            <span className="rounded-sm bg-action-subtle px-1.5 text-micro font-semibold text-action">
              {value.length}
            </span>
          ) : null}
          <Icon name="chevron-down" size={15} className="text-content-muted" />
        </span>
      </button>

      {open ? (
        <div
          className="absolute top-full z-30 mt-1 w-full min-w-[220px] rounded-lg border border-line bg-surface shadow-overlay"
          onKeyDown={onListKeyDown}
        >
          {options.length >= searchThreshold ? (
            <div className="border-b border-line-subtle p-2">
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
                placeholder={`Filter ${label.toLowerCase()}`}
                aria-label={`Filter ${label}`}
                className={cn(
                  'h-8 w-full rounded-md border border-line bg-surface px-2 text-dense-lg text-content placeholder:text-content-muted',
                  focusRing,
                )}
              />
            </div>
          ) : null}
          <ul
            id={listId}
            role="listbox"
            aria-multiselectable="true"
            aria-labelledby={`${baseId}-label`}
            tabIndex={-1}
            className="max-h-64 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-dense-lg text-content-muted">No matches</li>
            ) : (
              filtered.map((option, index) => {
                const selected = value.includes(option.value);
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      disabled={option.disabled}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => toggle(option.value)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-dense-lg text-content',
                        'hover:bg-surface-hover disabled:cursor-not-allowed disabled:text-content-disabled',
                        index === activeIndex && 'bg-surface-hover',
                        focusRing,
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                          selected
                            ? 'border-action bg-action text-action-contrast'
                            : 'border-line-strong bg-surface',
                        )}
                      >
                        {selected ? <Icon name="check" size={11} strokeWidth={2.5} /> : null}
                      </span>
                      <span className="truncate">{option.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {value.length > 0 ? (
            <div className="flex justify-between border-t border-line-subtle p-2">
              <button
                type="button"
                onClick={() => onChange([])}
                className={cn(
                  'rounded-sm px-1 text-dense text-content-link hover:underline',
                  focusRing,
                )}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={close}
                className={cn(
                  'rounded-sm px-1 text-dense text-content-secondary hover:underline',
                  focusRing,
                )}
              >
                Done
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {hint && !error ? <p className="text-dense text-content-muted">{hint}</p> : null}
      {error ? (
        <p className="flex items-center gap-1 text-dense text-danger">
          <Icon name="alert-circle" size={13} />
          {error}
        </p>
      ) : null}
    </div>
  );
});
